import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComp } from "@/components/ui/calendar";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function startOfWeek(d) {
  const x = new Date(d);
  x.setHours(0,0,0,0);
  x.setDate(x.getDate() - x.getDay());
  return x;
}

export default function CalendarPage() {
  const [appts, setAppts] = useState([]);
  const [date, setDate] = useState(new Date());

  useEffect(() => { (async () => {
    const { data } = await api.get("/appointments");
    setAppts(data);
  })(); }, []);

  const week = useMemo(() => {
    const start = startOfWeek(date);
    return [...Array(7)].map((_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [date]);

  const apptsByDay = useMemo(() => {
    const map = {};
    appts.forEach((a) => {
      const key = new Date(a.date).toDateString();
      (map[key] = map[key] || []).push(a);
    });
    return map;
  }, [appts]);

  const selectedDayAppts = useMemo(
    () => (apptsByDay[date.toDateString()] || []).sort((a, b) => new Date(a.date) - new Date(b.date)),
    [apptsByDay, date]
  );

  return (
    <div className="space-y-5 fade-up" data-testid="calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Calendar</h1>
          <p className="text-sm text-muted-foreground mt-1">Schedule, shifts and appointments</p>
        </div>
      </div>

      <Tabs defaultValue="week">
        <TabsList>
          <TabsTrigger value="week" data-testid="cal-week">Week</TabsTrigger>
          <TabsTrigger value="month" data-testid="cal-month">Month</TabsTrigger>
          <TabsTrigger value="day" data-testid="cal-day">Day</TabsTrigger>
        </TabsList>

        <TabsContent value="week">
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <div className="font-medium">{week[0].toLocaleDateString()} – {week[6].toLocaleDateString()}</div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => { const d = new Date(date); d.setDate(d.getDate() - 7); setDate(d); }}>
                    <ChevronLeft className="size-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDate(new Date())}>Today</Button>
                  <Button variant="ghost" size="icon" onClick={() => { const d = new Date(date); d.setDate(d.getDate() + 7); setDate(d); }}>
                    <ChevronRight className="size-4" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-7">
                {week.map((d) => {
                  const list = apptsByDay[d.toDateString()] || [];
                  const isToday = d.toDateString() === new Date().toDateString();
                  return (
                    <div key={d} className={`min-h-[200px] border-r border-b border-border last:border-r-0 p-3 ${isToday ? "bg-muted/40" : ""}`}>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                      <div className={`text-2xl font-bold ${isToday ? "text-primary" : ""}`}>{d.getDate()}</div>
                      <div className="mt-2 space-y-1">
                        {list.slice(0, 4).map((a) => (
                          <div key={a.id} className="text-[11px] p-1.5 bg-primary/10 text-primary rounded border-l-2 border-primary truncate">
                            {new Date(a.date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} • {a.patient_name}
                          </div>
                        ))}
                        {list.length > 4 && <div className="text-[10px] text-muted-foreground">+{list.length - 4} more</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="month">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <Card className="lg:col-span-1">
              <CardContent className="p-3">
                <CalendarComp mode="single" selected={date} onSelect={(d) => d && setDate(d)} className="rounded-md" />
              </CardContent>
            </Card>
            <Card className="lg:col-span-2">
              <CardContent className="p-5">
                <h3 className="font-semibold mb-3">{date.toLocaleDateString(undefined, { dateStyle: "full" })}</h3>
                <div className="space-y-2">
                  {selectedDayAppts.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 p-3 border border-border rounded-lg">
                      <div className="mono text-xs text-muted-foreground w-16">
                        {new Date(a.date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{a.patient_name}</div>
                        <div className="text-xs text-muted-foreground">{a.doctor_name} • {a.reason || "Consultation"}</div>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{a.status}</span>
                    </div>
                  ))}
                  {selectedDayAppts.length === 0 && <div className="text-sm text-muted-foreground text-center py-8">No appointments on this day.</div>}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="day">
          <Card>
            <CardContent className="p-5">
              <h3 className="font-semibold mb-4">{date.toLocaleDateString(undefined, { dateStyle: "full" })}</h3>
              <div className="space-y-1">
                {[...Array(12)].map((_, i) => {
                  const hour = i + 8;
                  const list = selectedDayAppts.filter((a) => new Date(a.date).getHours() === hour);
                  return (
                    <div key={hour} className="flex border-t border-border py-2">
                      <div className="w-20 text-xs text-muted-foreground mono">{`${hour}:00`}</div>
                      <div className="flex-1 space-y-1">
                        {list.map((a) => (
                          <div key={a.id} className="text-sm p-2 bg-primary/10 text-primary rounded border-l-2 border-primary">
                            {a.patient_name} • {a.doctor_name}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
